import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NspireWalkthrough from "@/components/NspireWalkthrough";
import DoctrineBanner from "@/components/DoctrineBanner";

const BACKEND_URL = "";

/**
 * ExpoLive — one-tap demo surface for the FWAA Expo booth.
 *
 * Two demo pathways, both reachable in a single tap from the Cockpit:
 *   1. 📷 Snap Photo   → opens the native rear camera immediately (no
 *                        crew join, no shift gate). Captured photos
 *                        preview inline so booth visitors can see the
 *                        digital platform actually receive field data.
 *   2. 🛡️ NSPIRE Walk  → renders the guided NSPIRE walkthrough
 *                        component full-screen. Room-by-room zones,
 *                        deficiency ledger, and Resolute Closeout
 *                        PDF download all live in one flow so a
 *                        visitor can watch the finished output
 *                        materialize.
 *
 * This screen is intentionally free of layout tricks, floating FABs,
 * or hidden nav. Wired to `/expo` and linked directly from Cockpit.
 */

const ExpoLive = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState("home"); // "home" | "walkthrough"
  const [address, setAddress] = useState("");
  const [inspector, setInspector] = useState("Shannon Ware");
  const [photos, setPhotos] = useState([]); // {dataUrl, timestamp, caption}
  const [uploading, setUploading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [processing, setProcessing] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [promptIdx, setPromptIdx] = useState(0);
  const autoProcessedRef = useRef(false);

  // ---- Feb 24, 2026 Comprehensive Upgrade State ----
  // Pre-walk footprint configuration — toggled amenities drive SOW add-ons.
  const [footprint, setFootprint] = useState({
    has_wd_hookups: false,
    has_balcony: false,
    has_dishwasher: false,
    has_garage: false,
    unit_size: "2bed",
    floor_level: "",
  });
  const [footprintReady, setFootprintReady] = useState(false);
  const [showFootprint, setShowFootprint] = useState(false);

  // Per-zone voice notes { [roomLabel]: verbatim text }
  const [zoneVoice, setZoneVoice] = useState({});
  const [voiceListening, setVoiceListening] = useState(false);
  const voiceRecogRef = useRef(null);

  // No-access flags: { [roomLabel]: { reason, at } }
  const [noAccess, setNoAccess] = useState({});

  // SOW result
  const [sow, setSow] = useState(null);
  const [sowBusy, setSowBusy] = useState(false);

  // Env mode toggle (sandbox suppresses vendor dispatch; live is production)
  const [envMode, setEnvMode] = useState(() => {
    try { return localStorage.getItem("mf_env_mode") || "sandbox"; } catch { return "sandbox"; }
  });
  const toggleEnvMode = () => {
    const next = envMode === "sandbox" ? "live" : "sandbox";
    setEnvMode(next);
    try { localStorage.setItem("mf_env_mode", next); } catch { /* noop */ }
  };

  // Load persisted footprint + zone voice + no-access from session
  useEffect(() => {
    try {
      const fp = sessionStorage.getItem("mf_expo_footprint");
      if (fp) { setFootprint(JSON.parse(fp)); setFootprintReady(true); }
      const zv = sessionStorage.getItem("mf_expo_zone_voice");
      if (zv) setZoneVoice(JSON.parse(zv));
      const na = sessionStorage.getItem("mf_expo_no_access");
      if (na) setNoAccess(JSON.parse(na));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try {
      sessionStorage.setItem("mf_expo_footprint", JSON.stringify(footprint));
      sessionStorage.setItem("mf_expo_zone_voice", JSON.stringify(zoneVoice));
      sessionStorage.setItem("mf_expo_no_access", JSON.stringify(noAccess));
    } catch { /* ignore */ }
  }, [footprint, zoneVoice, noAccess]);

  // ─── Live SOW preview · runs on every footprint toggle ────────────
  // Debounced 250ms so rapid toggling doesn't spam the backend.
  const [footprintPreview, setFootprintPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(async () => {
      setPreviewBusy(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/expo/preview-footprint`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            address: "preview",
            inspector: "preview",
            footprint,
            zones: [],
            env_mode: "sandbox",
          }),
        });
        if (r.ok) {
          setFootprintPreview(await r.json());
        }
      } catch { /* silent */ }
      finally { setPreviewBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [footprint]);

  const currentZone = () => GUIDED_PROMPTS[Math.min(promptIdx, GUIDED_PROMPTS.length - 1)];

  const zonePhotoCount = (roomLabel) => photos.filter(p => p.room === roomLabel).length;

  const toggleZoneVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setStatusMsg("Voice capture not supported on this browser."); return; }
    if (voiceListening && voiceRecogRef.current) {
      // User is stopping — mark intent so onend doesn't auto-restart
      voiceRecogRef.current._userStopped = true;
      try { voiceRecogRef.current.stop(); } catch { /* noop */ }
      voiceRecogRef.current = null;
      setVoiceListening(false);
      return;
    }
    const cz = currentZone().room;
    const startRecognition = () => {
      const r = new SR();
      r.continuous = true; r.interimResults = true; r.lang = "en-US";
      r._userStopped = false;
      r.onresult = (e) => {
        let final = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
        }
        if (final) setZoneVoice((prev) => ({ ...prev, [cz]: ((prev[cz] || "") + " " + final).trim() }));
      };
      r.onerror = (ev) => {
        // Auto-restart on transient errors (no-speech, aborted, network) UNLESS
        // the user explicitly stopped or the error is fatal (not-allowed).
        const fatal = ev?.error === "not-allowed" || ev?.error === "service-not-allowed";
        if (fatal || r._userStopped) {
          setVoiceListening(false);
          voiceRecogRef.current = null;
          if (fatal) setStatusMsg("Mic permission blocked. Enable microphone access and retry.");
        }
      };
      r.onend = () => {
        // Android Chrome auto-stops the mic after ~2s of silence even with
        // continuous=true. If the user hasn't stopped, silently restart so
        // the dictation feels continuous.
        if (!r._userStopped && voiceRecogRef.current === r) {
          try { r.start(); } catch {
            // If start() throws (already-started race), let it settle and try once more
            setTimeout(() => { try { r.start(); } catch { setVoiceListening(false); } }, 300);
          }
        } else {
          setVoiceListening(false);
        }
      };
      try { r.start(); } catch { /* noop */ }
      voiceRecogRef.current = r;
      setVoiceListening(true);
    };
    startRecognition();
  };

  const flagNoAccess = () => {
    // Auto-save with default reason and advance — no browser prompt loop.
    // If user wants to override reason, they tap "✎ Edit reason" on the
    // no-access badge that renders under the zone header.
    const cz = currentZone().room;
    setNoAccess((prev) => ({
      ...prev,
      [cz]: {
        reason: "Locked at inspection · auto-saved to ledger",
        at: new Date().toISOString(),
        auto_saved: true,
      },
    }));
    setStatusMsg(`🔒 ${cz} flagged as No Access · saved to ledger · auto-advancing to next zone.`);
    // Auto-advance so the flow doesn't stall on this exception.
    setTimeout(() => {
      setPromptIdx((i) => Math.min(i + 1, GUIDED_PROMPTS.length - 1));
    }, 900);
  };

  const setNoAccessReason = (zoneRoom, newReason) => {
    setNoAccess((prev) => ({
      ...prev,
      [zoneRoom]: {
        ...(prev[zoneRoom] || {}),
        reason: newReason || "Locked at inspection",
        at: new Date().toISOString(),
        auto_saved: false,
      },
    }));
  };

  const clearNoAccess = () => {
    const cz = currentZone().room;
    setNoAccess((prev) => {
      const n = { ...prev }; delete n[cz]; return n;
    });
  };

  const generateSow = async () => {
    if (sowBusy) return;
    setSowBusy(true);
    try {
      const zones = GUIDED_PROMPTS.map(p => ({
        zone:            p.room,
        doctrine:        p.doctrine,
        photo_count:     zonePhotoCount(p.room),
        voice_notes:     (zoneVoice[p.room] || "").trim(),
        no_access:       Boolean(noAccess[p.room]),
        no_access_reason: noAccess[p.room]?.reason || "",
      })).filter(z => z.photo_count > 0 || z.voice_notes || z.no_access);

      const r = await fetch(`${BACKEND_URL}/api/expo/generate-sow`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Env-Mode": envMode },
        body: JSON.stringify({
          address: address || "",
          inspector: inspector || "",
          footprint,
          zones,
          env_mode: envMode,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setStatusMsg(body.detail || `SOW generation failed (${r.status}).`); return; }
      setSow(body);
      setStatusMsg(`✓ SOW ${body.sow_id} compiled · ${body.line_count} lines · $${body.totals.grand_total_usd.toLocaleString()}`);
    } catch (e) {
      setStatusMsg(`SOW error: ${String(e.message || e)}`);
    } finally {
      setSowBusy(false);
    }
  };

  // Guided walkthrough prompts — one flows into the next with each
  // capture. Per Field Intake Patch Feb 12, 2026: NSPIRE + Section 8
  // + HUD-VASH doctrine woven into a single COO-driven sequence. The
  // first prompt is always "Entryway / Front Door" so the footprint
  // is initialized before any interior capture.
  const GUIDED_PROMPTS = [
    { room: "Entryway / Front Door",   focus: "Initialize footprint · Threshold seal · Deadbolt strike · MF-EXT-CLAMP mount plane",   doctrine: "NSPIRE",
      nspire_criteria:   ["Deadbolt operates", "Threshold weather-sealed", "Peephole clear at 42-66 in"],
      section8_criteria: ["Door secure with functioning lock", "No breach openings"] },
    { room: "Living Room",             focus: "Ceiling · smoke detector · AC grate · flooring transition",                              doctrine: "NSPIRE",
      nspire_criteria:   ["Smoke detector operational", "Ceiling free of stains/holes", "Flooring transitions flush"],
      section8_criteria: ["≥1 window opens", "Adequate natural or artificial light"] },
    { room: "Kitchen",                 focus: "Cabinets · countertops · cleared appliance bays · GFCI outlets · sink base",                    doctrine: "Section 8",
      nspire_criteria:   ["GFCI within 6 ft of sink", "Range hood or vent operational"],
      section8_criteria: ["Working stove burners", "Refrigerator cold", "Sink drains free"] },
    { room: "Primary Bathroom",        focus: "Tub · vanity · anti-scald · GFCI · exhaust fan · antimicrobial silicone joints",         doctrine: "NSPIRE",
      nspire_criteria:   ["Anti-scald valve installed", "GFCI within 6 ft of water", "Exhaust to exterior"],
      section8_criteria: ["Tub/shower drains", "Toilet flushes and refills", "Hot water present"] },
    { room: "Secondary Bathroom",      focus: "Tub or shower · vanity · GFCI · vent · caulk lines",                                     doctrine: "HUD-VASH",
      nspire_criteria:   ["GFCI receptacle present", "Ventilation to exterior"],
      section8_criteria: ["Fixtures functional", "No standing water"] },
    { room: "Bedroom 1 (Primary)",     focus: "Ceiling · smoke detector · closet doors · window ops · reach ranges",                    doctrine: "HUD-VASH",
      nspire_criteria:   ["Smoke detector present", "Egress window operates freely"],
      section8_criteria: ["≥1 window opens outward", "Door closes fully"] },
    { room: "Bedroom 2",               focus: "Ceiling · smoke detector · window ops · flooring transition",                            doctrine: "NSPIRE",
      nspire_criteria:   ["Smoke detector present", "Window locks intact"],
      section8_criteria: ["Adequate light + ventilation"] },
    { room: "Hallway / Utility",       focus: "Ceiling · HVAC access · water shut-off · smoke/CO detector coverage",                    doctrine: "NSPIRE",
      nspire_criteria:   ["CO detector present within 10 ft of sleeping areas", "HVAC filter clean"],
      section8_criteria: ["Utility access unobstructed"] },
    { room: "Exterior · Rear Door",    focus: "Threshold · MF-EXT-CLAMP mount · patio surface · fire-code egress",                      doctrine: "NSPIRE",
      nspire_criteria:   ["Rear egress unblocked", "Threshold sealed"],
      section8_criteria: ["Secondary exit functional"] },
    { room: "Exterior · Envelope",     focus: "Roof edge · siding · window glazing · foundation flashing",                              doctrine: "NSPIRE",
      nspire_criteria:   ["Siding integrity", "Window glazing intact", "No visible flashing gaps"],
      section8_criteria: ["Structural envelope sound"] },
    { room: "Deficiency Scan",         focus: "Any lingering violations · vagrant damage · vandalism · biological hazards",             doctrine: "NSPIRE",
      nspire_criteria:   ["Biohazards absent", "Pest evidence noted", "Squatter damage documented"],
      section8_criteria: ["Habitability confirmed"] },
    { room: "Owner Acclamation",       focus: "Final acclamation shot · signage · unit number placard",                                 doctrine: "Section 8",
      nspire_criteria:   ["Unit placard visible", "Address legible from street"],
      section8_criteria: ["Final walk-around complete"] },
  ];

  // Batch-review boundary — per Gemini doctrine reaffirmation Feb 12,
  // 2026: batch_threshold is null_unbounded_limit. Photos accumulate
  // unbounded. The 12-photo mark is a SOFT review boundary that auto-
  // fires the batch once, then continues capturing without cap. Every
  // capture also fires the engine with partial=true regardless of count.
  const AUTO_PROCESS_THRESHOLD = 12;

  useEffect(() => {
    // Load any previously captured demo photos + guided-prompt cursor
    // from session storage so the field walkthrough picks up where we
    // left off between visitors or between accidental tab reloads.
    try {
      const cached = sessionStorage.getItem("mf_expo_photos");
      if (cached) setPhotos(JSON.parse(cached));
      const cachedIdx = sessionStorage.getItem("mf_expo_prompt_idx");
      if (cachedIdx) setPromptIdx(parseInt(cachedIdx, 10) || 0);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    // Persist up to 60 photos so a long walkthrough survives a page
    // reload. Session storage still has quota, so we keep the newest
    // 60 rather than truncating the working set at 12.
    try {
      sessionStorage.setItem("mf_expo_photos", JSON.stringify(photos.slice(0, 60)));
      sessionStorage.setItem("mf_expo_prompt_idx", String(promptIdx));
    } catch (_) { /* ignore */ }
  }, [photos, promptIdx]);

  const openCamera = () => {
    // Opens the phone's native camera on mobile devices via the file
    // input's capture attribute. Falls back to file picker on desktop.
    // NOTE: click() MUST fire before any React state update — some
    // mobile browsers (Samsung Internet, older Android Chrome) drop
    // the camera permission gesture if a re-render happens between
    // the tap and the click().
    if (fileInputRef.current) fileInputRef.current.click();
    setStatusMsg("");
  };

  const onPhotoSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setStatusMsg("");
    try {
      const currentPrompt = GUIDED_PROMPTS[Math.min(promptIdx, GUIDED_PROMPTS.length - 1)];
      const roomLabel = currentPrompt?.room || "";
      const doctrineTag = currentPrompt?.doctrine || "";

      // ─── Corporate Resolute Framework v2026.09.19 · Clause 1 ───
      // Immutable Digital Evidence & Geotag Certification.
      // Capture GPS + UTC + SHA-256 hash on EVERY photo so the evidence
      // record is court-admissible tamper-evident proof of property
      // condition. Graceful degradation — if the browser denies GPS,
      // hash + UTC still land, and the record flags gps_available:false.
      const gpsSnapshot = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        const timeout = setTimeout(() => resolve(null), 6000);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            clearTimeout(timeout);
            resolve({
              lat:            pos.coords.latitude,
              lng:            pos.coords.longitude,
              accuracy_m:     pos.coords.accuracy,
              altitude_m:     pos.coords.altitude,
              heading_deg:    pos.coords.heading,
              speed_mps:      pos.coords.speed,
              fixed_at_utc:   new Date(pos.timestamp).toISOString(),
            });
          },
          () => { clearTimeout(timeout); resolve(null); },
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
      });

      // BATCH INTAKE — Feb 24, 2026 doctrine: unlimited photos per zone
      // in a single tap. Every file selected is tagged with the CURRENT
      // zone. Auto-advance is disabled — Shannon controls when to move
      // on via the "Next →" prompt control.
      const stampedBatch = [];
      for (const file of files) {
        const arrayBuf = await file.arrayBuffer();
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = () => rej(r.error);
          r.readAsDataURL(new Blob([arrayBuf]));
        });
        // SHA-256 · court-grade tamper-evident hash
        let sha256_hex = null;
        try {
          if (window.crypto?.subtle) {
            const digest = await window.crypto.subtle.digest("SHA-256", arrayBuf);
            sha256_hex = Array.from(new Uint8Array(digest))
              .map(b => b.toString(16).padStart(2, "0")).join("");
          }
        } catch { /* silent — hash stays null, other evidence still valid */ }

        const captured_at_utc = new Date().toISOString();
        stampedBatch.push({
          dataUrl,
          timestamp: captured_at_utc,
          room:      roomLabel,
          doctrine:  doctrineTag,
          caption:   [
            address || "",
            roomLabel,
            doctrineTag,
            new Date().toLocaleTimeString(),
          ].filter(Boolean).join(" · "),
          // ─── Court-Grade Resolute Framework · evidence block ───
          evidence: {
            sha256:            sha256_hex,
            captured_at_utc,
            gps:               gpsSnapshot,        // null if denied
            gps_available:     gpsSnapshot !== null,
            device_ua:         navigator.userAgent,
            file_size_bytes:   arrayBuf.byteLength,
            file_type:         file.type || "image/jpeg",
            file_name:         file.name || "",
            framework_version: "v2026.09.19_COURT_GRADE_SHIELD",
          },
        });
      }

      // No cap — every capture stays. Newest first.
      setPhotos(prev => [...stampedBatch.reverse(), ...prev]);

      // Fire-and-forget partial scoring — same as before, but now with
      // the full batch of newly captured photos.
      const capturedPhotos = [...stampedBatch, ...photos];
      fetch(`${BACKEND_URL}/api/engine/process-intake`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: null,
          photos: capturedPhotos.map((cp) => ({
            room:     cp.room     || "",
            doctrine: cp.doctrine || "",
            caption:  cp.caption  || "",
            timestamp: cp.timestamp,
            evidence:  cp.evidence || null,  // ← Court-Grade evidence block
          })),
          address:   address || "",
          unit_type: "2bed",
          inspector: inspector || "",
          partial:   true,
          auto_open_cockpit: false,
        }),
      }).then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d) return;
          if (d.compliance?.escort_required) {
            setStatusMsg(`⚠ Escort required · ${d.compliance.escort_roster.map(e => e.room).join(", ")}`);
          }
        })
        .catch(() => { /* silent — capture stays local */ });

      const zoneCount = capturedPhotos.filter(p => p.room === roomLabel).length;
      setStatusMsg(
        `✓ ${stampedBatch.length} photo${stampedBatch.length === 1 ? "" : "s"} added to ${roomLabel} · ${zoneCount} total for this zone. ` +
        (promptIdx + 1 < GUIDED_PROMPTS.length
          ? `Keep capturing or tap "Next →" for ${GUIDED_PROMPTS[promptIdx + 1].room}.`
          : `All zones covered — process the batch below.`)
      );
    } catch (err) {
      setStatusMsg("Could not read photo(s). Try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearPhotos = () => {
    setPhotos([]);
    setBatchResult(null);
    setPromptIdx(0);
    autoProcessedRef.current = false;
    try {
      sessionStorage.removeItem("mf_expo_photos");
      sessionStorage.removeItem("mf_expo_prompt_idx");
    } catch (_) { /* ignore */ }
  };

  // Process Batch & Open Design Studio — per Field Intake Patch
  // directive. POSTs the active session photo count/ids to the
  // render-from-intake endpoint, then fires `mf:scope-lock` so the
  // floating overlay + Cockpit inline render appear side-by-side.
  // If `auto_open_cockpit` returns true, navigate to /dashboard.
  const processBatch = async ({ autoOpen = true, autoTriggered = false } = {}) => {
    if (processing) return;
    if (photos.length === 0) {
      setStatusMsg("No photos in the batch yet. Snap at least one.");
      return;
    }
    setProcessing(true);
    setStatusMsg(autoTriggered
      ? `Threshold met (${photos.length} photos) — running Design Studio render…`
      : "Processing batch → Design Studio…");
    try {
      const r = await fetch(`${BACKEND_URL}/api/engine/process-intake`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: null,
          photos: photos.map((p, i) => ({
            room:      p.room     || "",
            doctrine:  p.doctrine || "",
            caption:   p.caption  || "",
            timestamp: p.timestamp,
          })),
          address:           address || "",
          unit_type:         "2bed",
          inspector:         inspector || "",
          partial:           false,
          auto_open_cockpit: !!autoOpen,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setBatchResult(d);
      // Fire the mf:scope-lock event so the floating overlay (App root)
      // and the Cockpit inline render pop up side-by-side without
      // manual navigation. Payload matches ScopeLockRender's expected
      // detail shape exactly.
      try {
        window.dispatchEvent(new CustomEvent("mf:scope-lock", { detail: d.scope_lock }));
      } catch (_) { /* ignore in non-browser env */ }
      setStatusMsg(`✓ ${d.message}`);
      if (d.auto_open_cockpit) {
        // Brief pause so the visitor sees the "processed" confirmation
        // land before the route change animates in.
        setTimeout(() => navigate("/dashboard"), 900);
      }
    } catch (err) {
      setStatusMsg(`Batch process failed: ${err.message || err}. Photos stay in the queue.`);
    } finally {
      setProcessing(false);
    }
  };

  // Auto-process effect — fires once when the session first hits the
  // 12-photo threshold. Owner can still tap the button manually before
  // then, or re-tap after clearing.
  useEffect(() => {
    if (autoProcessedRef.current) return;
    if (photos.length >= AUTO_PROCESS_THRESHOLD && !processing && !batchResult) {
      autoProcessedRef.current = true;
      processBatch({ autoOpen: true, autoTriggered: true });
    }
     
  }, [photos.length]);

  if (mode === "walkthrough") {
    return (
      <div style={pageStyle}>
        <div style={headerBar}>
          <button data-testid="expo-back-to-home" onClick={() => setMode("home")} style={backBtn}>
            ← Expo Home
          </button>
          <div style={titleBlock}>
            <div style={eyebrow}>MOOVIN FORWARD · LIVE DEMO</div>
            <h1 style={h1}>NSPIRE Walkthrough</h1>
          </div>
        </div>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 16px 40px" }}>
          <NspireWalkthrough
            propertyAddress={address || "Fossil Creek 932 · Unit 5960 Siltstone Lane"}
            entityId=""
            inspector={inspector}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle} data-testid="expo-live-home">
      <div style={headerBar}>
        <button onClick={() => navigate("/dashboard")} style={backBtn} data-testid="expo-back-to-dash">
          ← Dashboard
        </button>
        <div style={titleBlock}>
          <div style={eyebrow}>MOOVIN FORWARD · OPERATIONAL COCKPIT</div>
          <h1 style={h1}>Field → Scope → Render · One Ecosystem</h1>
          <div style={{ maxWidth: 900, margin: "8px auto 0" }}>
            <DoctrineBanner compact />
          </div>
          <p style={subLead}>
            Camera opens rear-facing. Every capture routes to NSPIRE walkthrough,
            auto-tags violations, compiles the scope + acclamation product variations,
            and outputs the side-by-side render with the 100/50 procurement ledger.
            One trigger. COO-driven. No isolated tools.
          </p>
        </div>
        {/* SANDBOX / LIVE mode toggle — controls vendor-dispatch suppression */}
        <button data-testid="expo-env-toggle" onClick={toggleEnvMode}
                title="Sandbox suppresses vendor dispatch. Live enables production routing."
                style={{
                  position: "absolute", top: 12, right: 12,
                  background: envMode === "live" ? "#22c55e" : "#F59E0B",
                  color: "#0b1220", border: "none", borderRadius: 999,
                  padding: "6px 14px", fontSize: 11, fontWeight: 900,
                  letterSpacing: "0.14em", cursor: "pointer",
                  boxShadow: envMode === "live" ? "0 0 12px rgba(34,197,94,0.5)" : "0 0 12px rgba(245,158,11,0.35)",
                }}>
          {envMode === "live" ? "🟢 LIVE" : "🟠 SANDBOX"}
        </button>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 16px 40px" }}>
        {/* Guided walkthrough banner — the current prompt drives every
            capture forward through the NSPIRE / Section 8 / HUD-VASH
            sequence. Starts on Entryway to initialize the footprint. */}
        <div data-testid="expo-guided-prompt" style={promptBanner}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.2em", fontWeight: 800 }}>
                STEP {Math.min(promptIdx + 1, GUIDED_PROMPTS.length)} OF {GUIDED_PROMPTS.length} · {currentZone().doctrine}
              </div>
              <div style={{ color: "#fff", fontSize: "1.15rem", fontWeight: 800, marginTop: 4 }}>
                {promptIdx === 0 ? "Scan Entryway / Front Door to Initialize Footprint" : currentZone().room}
              </div>
              <div style={{ color: "#ADBED1", fontSize: "0.78rem", marginTop: 3 }}>
                {currentZone().focus}
              </div>

              {/* NSPIRE + Section 8 criteria HUD */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
                <div style={{ background: "rgba(0,255,255,0.06)", border: "1px solid rgba(0,255,255,0.25)", borderRadius: 6, padding: "8px 10px" }}>
                  <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: "0.18em", fontWeight: 800, marginBottom: 4 }}>NSPIRE CRITERIA</div>
                  {(currentZone().nspire_criteria || []).map((c, i) => (
                    <div key={i} style={{ color: "#D1D5DB", fontSize: 11, lineHeight: 1.5 }}>· {c}</div>
                  ))}
                </div>
                <div style={{ background: "rgba(0,220,120,0.06)", border: "1px solid rgba(0,220,120,0.25)", borderRadius: 6, padding: "8px 10px" }}>
                  <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: "0.18em", fontWeight: 800, marginBottom: 4 }}>SECTION 8 HQS</div>
                  {(currentZone().section8_criteria || []).map((c, i) => (
                    <div key={i} style={{ color: "#D1D5DB", fontSize: 11, lineHeight: 1.5 }}>· {c}</div>
                  ))}
                </div>
              </div>

              {/* Live per-zone photo counter + no-access badge */}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
                <span data-testid="expo-zone-counter"
                      style={{ background: "#111827", border: "1px solid #374151", borderRadius: 999,
                               padding: "3px 10px", color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace",
                               fontSize: 11, fontWeight: 800, letterSpacing: "0.08em" }}>
                  📸 {zonePhotoCount(currentZone().room)} in this zone
                </span>
                {noAccess[currentZone().room] && (
                  <span data-testid="expo-noaccess-badge"
                        style={{ background: "#F59E0B", color: "#0b1220", borderRadius: 999,
                                 padding: "3px 10px", fontSize: 10, fontWeight: 900, letterSpacing: "0.15em" }}>
                    🔒 NO ACCESS · {noAccess[currentZone().room].reason}
                  </span>
                )}
                {(zoneVoice[currentZone().room] || "").trim() && (
                  <span style={{ background: "rgba(134,239,172,0.15)", color: "#86EFAC",
                                 border: "1px solid rgba(134,239,172,0.4)", borderRadius: 999,
                                 padding: "3px 10px", fontSize: 10, fontWeight: 800, letterSpacing: "0.1em" }}>
                    🎙 VOICE NOTES
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                onClick={() => setPromptIdx(i => Math.max(0, i - 1))}
                disabled={promptIdx === 0}
                data-testid="expo-prompt-prev"
                style={promptStepBtn(promptIdx === 0)}>
                ← Prev
              </button>
              <button
                onClick={() => setPromptIdx(i => Math.min(GUIDED_PROMPTS.length - 1, i + 1))}
                disabled={promptIdx >= GUIDED_PROMPTS.length - 1}
                data-testid="expo-prompt-next"
                style={promptStepBtn(promptIdx >= GUIDED_PROMPTS.length - 1)}>
                Next →
              </button>
            </div>
          </div>
        </div>

        {/* Pre-walk footprint gate — first-time only. Toggles amenities. */}
        {!footprintReady && (
          <div data-testid="expo-footprint-gate"
               style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.35)",
                        borderRadius: 10, padding: "14px 16px", marginTop: 14 }}>
            <div style={{ color: "#F59E0B", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: "0.2em", fontWeight: 800, marginBottom: 6 }}>
              PRE-WALK FOOTPRINT · CONFIGURE BEFORE FIRST CAPTURE
            </div>
            <div style={{ color: "#D1D5DB", fontSize: 13, marginBottom: 10 }}>
              Toggle any amenities present in this unit. Each toggle adds a tailored line item to the auto-generated Scope of Work.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
              {[
                ["has_wd_hookups", "🧺 W/D hookups"],
                ["has_balcony", "🌆 Balcony / patio"],
                ["has_dishwasher", "🍽 Dishwasher"],
                ["has_garage", "🚗 Garage access"],
              ].map(([k, lbl]) => (
                <label key={k} data-testid={`expo-fp-${k}`}
                       style={{ display: "flex", alignItems: "center", gap: 8, background: "#111827",
                                border: `1px solid ${footprint[k] ? "#F59E0B" : "#374151"}`, borderRadius: 6,
                                padding: "8px 10px", cursor: "pointer", fontSize: 13, color: "#F3F4F6" }}>
                  <input type="checkbox" checked={footprint[k]}
                         onChange={() => setFootprint(fp => {
                           const next = { ...fp, [k]: !fp[k] };
                           const count = ["has_wd_hookups","has_balcony","has_dishwasher","has_garage"].filter(kk => next[kk]).length;
                           setStatusMsg(`Amenity toggled · ${count} SOW line item${count === 1 ? "" : "s"} queued. Tap Compile SOW to persist.`);
                           return next;
                         })} />
                  {lbl}
                </label>
              ))}
            </div>
            {/* Live SOW preview — proves the toggles are wired end-to-end.
                Every checkbox click hits /api/expo/preview-footprint and
                renders the actual line items with SKUs + prices. */}
            <div data-testid="expo-fp-live-preview" style={{ marginTop: 12 }}>
              {previewBusy && (
                <div style={{ fontSize: 11, color: "#94A3B8", padding: "6px 0" }}>
                  ⚙ Computing SOW preview…
                </div>
              )}
              {footprintPreview && footprintPreview.line_count > 0 ? (
                <div style={{
                  background: "rgba(34, 197, 94, 0.08)",
                  border: "1px solid rgba(34, 197, 94, 0.4)",
                  borderRadius: 8, padding: "10px 12px",
                }}>
                  <div style={{
                    fontSize: 10, letterSpacing: "0.16em", fontWeight: 800,
                    color: "#22c55e", marginBottom: 8,
                  }}>
                    ✓ LIVE PREVIEW · {footprintPreview.line_count} LINE ITEM{footprintPreview.line_count === 1 ? "" : "S"} FROM BACKEND
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    {footprintPreview.amenity_lines.map((l, i) => (
                      <div key={l.sku + i} data-testid={`expo-fp-preview-row-${l.toggle_key}`}
                           style={{
                             display: "grid", gridTemplateColumns: "auto 1fr auto",
                             gap: 10, alignItems: "center",
                             fontSize: 12, color: "#F3F4F6",
                             padding: "4px 0",
                             borderBottom: i < footprintPreview.amenity_lines.length - 1 ? "1px dashed rgba(255,255,255,0.08)" : "none",
                           }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#00CFCF", fontSize: 10, fontWeight: 800 }}>
                          {l.sku}
                        </span>
                        <span>{l.description} <span style={{ color: "#94A3B8" }}>× {l.qty}</span></span>
                        <span style={{ color: "#86EFAC", fontWeight: 800 }}>
                          ${Number(l.line_total_usd).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    marginTop: 8, paddingTop: 8,
                    borderTop: "1px solid rgba(34,197,94,0.3)",
                    fontSize: 12,
                  }}>
                    <span style={{ color: "#94A3B8" }}>Amenity subtotal + {footprintPreview.labor_hours}h labor</span>
                    <span style={{ color: "#22c55e", fontWeight: 800 }}>
                      ${Number(footprintPreview.amenity_subtotal + footprintPreview.labor_cost_usd).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 6, fontStyle: "italic" }}>
                    Preview only · not persisted. Tap <strong>📄 COMPILE SOW</strong> to commit full walkthrough.
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: "8px 12px",
                  background: "rgba(148, 163, 184, 0.06)",
                  border: "1px dashed rgba(148, 163, 184, 0.3)",
                  borderRadius: 6, fontSize: 12, color: "#94A3B8",
                }}>
                  Toggle any amenity above → live SOW line items will appear here from the backend.
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
              <label style={{ color: "#94A3B8", fontSize: 11, letterSpacing: "0.12em" }}>UNIT SIZE</label>
              <select value={footprint.unit_size}
                      onChange={(e) => setFootprint(fp => ({ ...fp, unit_size: e.target.value }))}
                      data-testid="expo-fp-size"
                      style={{ background: "#111827", color: "#F3F4F6", border: "1px solid #374151",
                               borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
                <option value="studio">Studio</option>
                <option value="1bed">1 bed</option>
                <option value="2bed">2 bed</option>
                <option value="3bed">3 bed</option>
                <option value="4bed">4 bed</option>
              </select>
              <input placeholder="Floor / level (optional)" value={footprint.floor_level}
                     onChange={(e) => setFootprint(fp => ({ ...fp, floor_level: e.target.value }))}
                     data-testid="expo-fp-floor"
                     style={{ background: "#111827", color: "#F3F4F6", border: "1px solid #374151",
                              borderRadius: 6, padding: "6px 10px", fontSize: 12, flex: 1 }} />
              <button data-testid="expo-fp-lock" onClick={() => setFootprintReady(true)}
                      style={{ background: "#F59E0B", color: "#0b1220", border: "none",
                               borderRadius: 6, padding: "8px 14px", fontWeight: 800, fontSize: 12,
                               letterSpacing: "0.1em", cursor: "pointer" }}>
                LOCK & START WALK
              </button>
            </div>
          </div>
        )}
        {footprintReady && (
          <button data-testid="expo-fp-edit" onClick={() => setFootprintReady(false)}
                  style={{ background: "transparent", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.5)",
                           borderRadius: 6, padding: "5px 10px", fontSize: 11, marginTop: 8,
                           fontWeight: 700, cursor: "pointer", letterSpacing: "0.06em" }}>
            ⚙ EDIT FOOTPRINT · {footprint.unit_size} · {[
              footprint.has_wd_hookups && "W/D",
              footprint.has_balcony && "Balcony",
              footprint.has_dishwasher && "DW",
              footprint.has_garage && "Garage",
            ].filter(Boolean).join(" · ") || "no amenities"}
          </button>
        )}

        {/* Property context (optional but useful for the demo) */}
        <div style={contextCard}>
          <label style={label}>PROPERTY / UNIT LABEL (SHOWN IN CAPTIONS)</label>
          <input
            type="text"
            data-testid="expo-address-input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. Fossil Creek · Unit 932"
            style={input}
          />
          <label style={{ ...label, marginTop: 12 }}>INSPECTOR NAME</label>
          <input
            type="text"
            data-testid="expo-inspector-input"
            value={inspector}
            onChange={(e) => setInspector(e.target.value)}
            style={input}
          />
        </div>

        {/* Two big gateway buttons */}
        <div style={gatewayGrid}>
          <button
            onClick={openCamera}
            data-testid="expo-camera-btn"
            disabled={uploading || !footprintReady}
            title={!footprintReady ? "Lock footprint before capturing" : ""}
            style={{ ...gatewayBtn, background: "linear-gradient(135deg, #5AC8D8 0%, #3AA8B8 100%)", opacity: !footprintReady ? 0.5 : 1 }}
          >
            <div style={gatewayIcon}>📷</div>
            <div style={gatewayLabel}>{promptIdx === 0 ? "Scan Entryway" : `Capture · ${GUIDED_PROMPTS[Math.min(promptIdx, GUIDED_PROMPTS.length - 1)].room}`}</div>
            <div style={gatewaySub}>Batch capture · unlimited photos per zone · auto-tags {GUIDED_PROMPTS[Math.min(promptIdx, GUIDED_PROMPTS.length - 1)].doctrine} doctrine.</div>
          </button>

          <button
            onClick={() => setMode("walkthrough")}
            data-testid="expo-walkthrough-btn"
            style={{ ...gatewayBtn, background: "linear-gradient(135deg, #00DC78 0%, #00B364 100%)" }}
          >
            <div style={gatewayIcon}>🛡️</div>
            <div style={gatewayLabel}>NSPIRE Walkthrough</div>
            <div style={gatewaySub}>Room-by-room inspection + Resolute Closeout PDF.</div>
          </button>
        </div>

        {/* Inspector Assistant toolbar — voice dictation + no-access flag + SOW */}
        <div data-testid="expo-inspector-toolbar"
             style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: 8, marginTop: 12 }}>
          <button data-testid="expo-voice-toggle" onClick={toggleZoneVoice}
                  style={{ background: voiceListening ? "#86EFAC" : "#0b1220",
                           color: voiceListening ? "#0b1220" : "#86EFAC",
                           border: `1px solid ${voiceListening ? "#86EFAC" : "rgba(134,239,172,0.5)"}`,
                           borderRadius: 8, padding: "12px 10px", fontWeight: 800, fontSize: 13,
                           letterSpacing: "0.06em", cursor: "pointer" }}>
            {voiceListening ? "⏹ STOP DICTATION" : "🎙 VOICE-TO-LEDGER"}
          </button>
          <button data-testid="expo-noaccess-btn"
                  onClick={noAccess[currentZone().room] ? clearNoAccess : flagNoAccess}
                  style={{ background: noAccess[currentZone().room] ? "#F59E0B" : "#0b1220",
                           color: noAccess[currentZone().room] ? "#0b1220" : "#F59E0B",
                           border: `1px solid ${noAccess[currentZone().room] ? "#F59E0B" : "rgba(245,158,11,0.5)"}`,
                           borderRadius: 8, padding: "12px 10px", fontWeight: 800, fontSize: 13,
                           letterSpacing: "0.06em", cursor: "pointer" }}>
            {noAccess[currentZone().room] ? "✓ CLEAR NO-ACCESS" : "🔒 NO ACCESS / LOCKED"}
          </button>
          <button data-testid="expo-sow-btn" onClick={generateSow} disabled={sowBusy}
                  style={{ background: sowBusy ? "#374151" : "linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)",
                           color: sowBusy ? "#94A3B8" : "#fff", border: "none",
                           borderRadius: 8, padding: "12px 10px", fontWeight: 800, fontSize: 13,
                           letterSpacing: "0.06em", cursor: sowBusy ? "wait" : "pointer" }}>
            {sowBusy ? "COMPILING…" : "📄 COMPILE SOW"}
          </button>
        </div>

        {/* Live voice-notes textarea for the current zone */}
        {(zoneVoice[currentZone().room] || voiceListening) && (
          <div data-testid="expo-voice-panel"
               style={{ marginTop: 10, background: "#0b1220",
                        border: `1px solid ${voiceListening ? "#86EFAC" : "#374151"}`,
                        borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ color: "#94A3B8", fontSize: 10, letterSpacing: "0.14em", fontWeight: 700 }}>
                VOICE LEDGER · {currentZone().room}
              </div>
              {voiceListening && <span style={{ color: "#86EFAC", fontSize: 10, fontWeight: 800 }}>● LISTENING</span>}
            </div>
            <textarea value={zoneVoice[currentZone().room] || ""}
                      onChange={(e) => setZoneVoice(prev => ({ ...prev, [currentZone().room]: e.target.value }))}
                      rows={2}
                      placeholder="Speak or type observations — e.g., 'Hairline crack west wall, HVAC intake soiled, GFCI missing at vanity.'"
                      style={{ width: "100%", background: "transparent", color: "#F3F4F6",
                               border: "none", outline: "none", fontSize: 13, resize: "vertical",
                               fontFamily: "inherit" }} />
          </div>
        )}

        {statusMsg && (
          <div data-testid="expo-status" style={statusPill}>{statusMsg}</div>
        )}

        {/* Captured photo strip — proof the platform is receiving field data */}
        {photos.length > 0 && (
          <div style={photoSection} data-testid="expo-photo-strip">
            <div style={photoHeader}>
              <div>
                <div style={eyebrow}>DIGITAL PLATFORM · FIELD INTAKE</div>
                <div style={{ color: "#fff", fontSize: 18, fontWeight: 800 }}>
                  {photos.length} photo{photos.length === 1 ? "" : "s"} captured this session
                </div>
                {photos.length < AUTO_PROCESS_THRESHOLD && (
                  <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 4, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}>
                    {AUTO_PROCESS_THRESHOLD - photos.length} more to auto-fire batch → Design Studio
                  </div>
                )}
              </div>
              <button onClick={clearPhotos} data-testid="expo-clear-photos" style={clearBtn}>Clear</button>
            </div>

            {/* Process Batch & Open Design Studio — primary CTA per
                Field Intake Patch directive. Ships the active session
                photo batch into /api/design-studio/render-from-intake,
                which fires an mf:scope-lock event and (if enabled)
                jumps to Cockpit for the side-by-side render. */}
            <button
              onClick={() => processBatch({ autoOpen: true })}
              disabled={processing || photos.length === 0}
              data-testid="expo-process-batch-btn"
              style={{
                width: "100%",
                marginTop: 12,
                padding: "14px 16px",
                background: processing
                  ? "rgba(0,220,120,0.35)"
                  : "linear-gradient(90deg, #00DC78 0%, #00B364 100%)",
                color: "#03151F",
                border: "none",
                borderRadius: 10,
                fontWeight: 900,
                fontSize: "0.98rem",
                letterSpacing: "0.02em",
                cursor: processing ? "not-allowed" : "pointer",
                boxShadow: "0 10px 24px rgba(0,220,120,0.22)",
              }}>
              {processing
                ? "⏳ Processing batch → Design Studio…"
                : `▶ Process Batch (${photos.length}) & Open Design Studio`}
            </button>

            {batchResult && (
              <div data-testid="expo-batch-result" style={batchResultCard}>
                <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.18em", fontWeight: 800 }}>
                  ✓ BATCH PROCESSED · {batchResult.batch_size} PHOTOS
                </div>
                <div style={{ color: "#fff", fontSize: "0.92rem", fontWeight: 800, marginTop: 4 }}>
                  {batchResult.scope_lock?.stream_label}
                </div>
                <div style={{ color: "#5AC8D8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", marginTop: 4 }}>
                  Line total ${(batchResult.scope_lock?.line_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                  {batchResult.scope_lock?.financial_split && (
                    <span style={{ color: "#B4BEC8", marginLeft: 8, fontSize: "0.72rem" }}>
                      · owner half ${(batchResult.scope_lock.financial_split.half_of_line_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div style={photoGrid}>
              {photos.map((p, idx) => (
                <div key={idx} style={photoCard} data-testid={`expo-photo-${idx}`}>
                  <img src={p.dataUrl} alt={`capture-${idx}`} style={photoImg} />
                  <div style={photoCaption}>{p.caption}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Silent file input triggered by the camera button.
            multiple=true → unlimited photos per zone in one press.
            NOTE: Samsung Internet + older Android Chrome refuse to
            trigger the camera picker on `display:none` inputs. Using
            offscreen positioning instead keeps the element rendered
            (so browsers trust the click gesture) while invisible. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={onPhotoSelected}
          data-testid="expo-photo-input"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            opacity: 0,
            overflow: "hidden",
            pointerEvents: "none",
            left: -9999,
          }}
        />

        {/* SOW Result Panel — contractor-ready line-item scope */}
        {sow && (
          <div data-testid="expo-sow-panel"
               style={{ marginTop: 20, background: "rgba(167,139,250,0.06)",
                        border: "1px solid rgba(167,139,250,0.35)",
                        borderLeft: "4px solid #A78BFA",
                        borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ color: "#A78BFA", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: "0.2em", fontWeight: 800 }}>
                  SCOPE OF WORK · CONTRACTOR REVIEW REQUIRED
                </div>
                <div style={{ color: "#fff", fontSize: 16, fontWeight: 800, marginTop: 4 }}>
                  {sow.sow_id} · {sow.line_count} line{sow.line_count === 1 ? "" : "s"} · ${sow.totals.grand_total_usd.toLocaleString()}
                </div>
                <div style={{ color: "#94A3B8", fontSize: 11, marginTop: 4 }}>
                  Trades engaged: {sow.trade_lanes.join(", ") || "none"} ·
                  Vendor dispatch: <span style={{ color: sow.env_mode === "live" ? "#22c55e" : "#F59E0B", fontWeight: 800 }}>{sow.vendor_dispatch_status}</span>
                </div>
              </div>
              <button data-testid="expo-sow-close" onClick={() => setSow(null)}
                      style={{ background: "transparent", color: "#94A3B8",
                               border: "1px solid #374151", borderRadius: 6,
                               padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>
                × close
              </button>
            </div>

            {/* Life-safety + no-access flag chips */}
            {(sow.life_safety_flags.length > 0 || sow.no_access_flags.length > 0) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                {sow.life_safety_flags.map((f, i) => (
                  <span key={`ls-${i}`} style={{ background: "#EF4444", color: "#0b1220", borderRadius: 3,
                                                 padding: "2px 8px", fontSize: 10, fontWeight: 900, letterSpacing: "0.1em" }}>
                    LIFE-SAFETY · {f.zone} · {f.sku}
                  </span>
                ))}
                {sow.no_access_flags.map((f, i) => (
                  <span key={`na-${i}`} style={{ background: "#F59E0B", color: "#0b1220", borderRadius: 3,
                                                 padding: "2px 8px", fontSize: 10, fontWeight: 900, letterSpacing: "0.1em" }}>
                    NO ACCESS · {f.zone}
                  </span>
                ))}
              </div>
            )}

            {/* Line items */}
            <div style={{ marginTop: 12, background: "#0b1220", border: "1px solid #1f2937",
                          borderRadius: 6, padding: 6, maxHeight: 320, overflowY: "auto" }}>
              {sow.line_items.map(li => (
                <div key={li.line_id}
                     style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px",
                              borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#F3F4F6", fontWeight: 700 }}>{li.description}</div>
                    <div style={{ color: "#94A3B8", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, marginTop: 2 }}>
                      {li.zone} · {li.trade_lane} · {li.sku} · qty {li.qty} · {li.labor_hours}h
                      {li.voice_evidence && <span style={{ color: "#86EFAC" }}> · 🎙</span>}
                      {li.photo_evidence_count > 0 && <span style={{ color: "#5AC8D8" }}> · 📸 {li.photo_evidence_count}</span>}
                    </div>
                  </div>
                  <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, whiteSpace: "nowrap" }}>
                    ${li.line_total_usd.toLocaleString()}
                  </div>
                </div>
              ))}
              {sow.line_items.length === 0 && (
                <div style={{ padding: 14, color: "#94A3B8", textAlign: "center", fontSize: 12 }}>
                  No line items compiled — capture photos, dictate notes, or toggle amenities first.
                </div>
              )}
            </div>

            {/* Totals */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginTop: 10 }}>
              {[
                ["MATERIAL", `$${sow.totals.material_subtotal_usd.toLocaleString()}`],
                ["LABOR HRS", `${sow.totals.labor_hours}`],
                ["LABOR $", `$${sow.totals.labor_cost_usd.toLocaleString()}`],
                ["GRAND TOTAL", `$${sow.totals.grand_total_usd.toLocaleString()}`],
              ].map(([l, v]) => (
                <div key={l} style={{ background: "#111827", border: "1px solid #1f2937",
                                       borderRadius: 6, padding: "8px 6px", textAlign: "center" }}>
                  <div style={{ color: "#94A3B8", fontSize: 9, letterSpacing: "0.14em", fontWeight: 800 }}>{l}</div>
                  <div style={{ color: l === "GRAND TOTAL" ? "#00FFFF" : "#F3F4F6", fontWeight: 800, fontSize: 14, marginTop: 2 }}>{v}</div>
                </div>
              ))}
            </div>

            <div style={{ color: "#ADBED1", fontSize: 11, marginTop: 10, lineHeight: 1.5, fontStyle: "italic" }}>
              Contractors accepting a work order sourced from this SOW must acknowledge every line item first.
              {sow.env_mode === "sandbox" && " Sandbox mode is on — no vendor SMS or email will fire."}
            </div>
          </div>
        )}

        {/* Doctrine reminder for the visitor */}
        <div style={doctrineNote}>
          Every capture routes through the same Resolute Closeout pipeline that generates the court-grade PDF you download at shift end. No isolated tools. One continuous organism.
        </div>
      </div>
    </div>
  );
};

// ─── styles ────────────────────────────────────────────────────────
const pageStyle = { minHeight: "100vh", background: "#030B1A", color: "#fff", paddingBottom: 40 };
const headerBar = {
  padding: "24px 16px 20px",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  background: "linear-gradient(180deg, rgba(0,255,255,0.04) 0%, rgba(3,11,26,0) 100%)",
  position: "relative",
};
const titleBlock = { maxWidth: 900, margin: "0 auto", padding: "6px 0 0" };
const eyebrow = {
  color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.7rem", letterSpacing: "0.24em", fontWeight: 800,
};
const h1 = { fontSize: "1.8rem", fontWeight: 900, letterSpacing: "-0.01em", margin: "6px 0 6px" };
const subLead = { color: "#ADBED1", fontSize: "0.95rem", margin: 0, lineHeight: 1.5 };
const backBtn = {
  background: "transparent", border: "1px solid rgba(232,184,196,0.5)",
  color: "#E8B8C4", padding: "8px 14px", borderRadius: 999,
  fontWeight: 800, fontSize: 12, letterSpacing: "0.02em", cursor: "pointer",
};
const promptBanner = {
  marginTop: 18,
  padding: "14px 16px",
  background: "linear-gradient(180deg, rgba(0,255,255,0.06) 0%, rgba(0,220,120,0.04) 100%)",
  border: "1px solid rgba(0,255,255,0.32)",
  borderRadius: 10,
};
const promptStepBtn = (disabled) => ({
  background: disabled ? "transparent" : "rgba(0,255,255,0.08)",
  color: disabled ? "#4B5D6E" : "#00FFFF",
  border: `1px solid ${disabled ? "rgba(255,255,255,0.08)" : "rgba(0,255,255,0.35)"}`,
  padding: "6px 10px",
  borderRadius: 6,
  fontSize: "0.7rem",
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: "0.08em",
  fontWeight: 700,
  cursor: disabled ? "not-allowed" : "pointer",
});
const batchResultCard = {
  marginTop: 12,
  padding: "10px 12px",
  background: "rgba(0,220,120,0.08)",
  border: "1px solid rgba(0,220,120,0.35)",
  borderRadius: 8,
};
const contextCard = {
  background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 10, padding: "16px 18px", marginTop: 20,
};
const label = {
  display: "block", color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.68rem", letterSpacing: "0.18em", fontWeight: 800, marginBottom: 6,
};
const input = {
  width: "100%", padding: "10px 12px", background: "rgba(0,0,0,0.35)",
  border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#fff",
  fontSize: 15, boxSizing: "border-box",
};
const gatewayGrid = {
  display: "grid", gridTemplateColumns: "1fr", gap: 14, marginTop: 20,
};
const gatewayBtn = {
  border: "none", borderRadius: 14, padding: "22px 20px",
  color: "#03151F", cursor: "pointer", textAlign: "left",
  boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
  transition: "transform 0.15s",
};
const gatewayIcon = { fontSize: 42, marginBottom: 6 };
const gatewayLabel = { fontSize: 22, fontWeight: 900, letterSpacing: "-0.01em" };
const gatewaySub = { fontSize: 13, fontWeight: 700, opacity: 0.75, marginTop: 4 };
const statusPill = {
  marginTop: 16, padding: "10px 14px",
  background: "rgba(0,220,120,0.10)", border: "1px solid rgba(0,220,120,0.4)",
  borderRadius: 8, color: "#00DC78", fontSize: 13, fontWeight: 700,
};
const photoSection = {
  marginTop: 24, padding: "16px 16px 18px",
  background: "rgba(0,255,255,0.03)", border: "1px solid rgba(0,255,255,0.20)",
  borderRadius: 12,
};
const photoHeader = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  marginBottom: 12, gap: 12, flexWrap: "wrap",
};
const clearBtn = {
  background: "transparent", color: "#F87171",
  border: "1px solid rgba(248,113,113,0.5)", borderRadius: 999,
  padding: "6px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer",
};
const photoGrid = {
  display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
  gap: 10,
};
const photoCard = {
  borderRadius: 8, overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.4)",
};
const photoImg = { width: "100%", height: 120, objectFit: "cover", display: "block" };
const photoCaption = {
  padding: "6px 8px", fontSize: 11, color: "#ADBED1",
  fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.02em",
};
const doctrineNote = {
  marginTop: 28, padding: "14px 16px",
  background: "rgba(232,184,196,0.06)", border: "1px solid rgba(232,184,196,0.25)",
  borderRadius: 8, color: "#E8B8C4",
  fontSize: 13, lineHeight: 1.5, fontStyle: "italic",
};

export default ExpoLive;
